import { Link } from "react-router";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import Picture from "../components/Picture/Picture.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import { surfaceFor } from "../lib/surface.js";
import styles from "./rsa3d-why.module.css";

const PAGE = {
  title: "Why Road Safety Audits",
  description:
    "What a road safety audit is, why it matters for new designs and existing roads, and how a 3D audit finds hazards without waiting for crash history.",
  path: "/road-safety-audit-3d/why-road-safety-audits",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Road Safety Audit 3D", path: "/road-safety-audit-3d" },
  { name: "Why road safety audits", path: PAGE.path },
];

export function meta() {
  return buildMeta({
    ...PAGE,
    image: "/images/123bimlive/ssd",
    jsonLd: [breadcrumbJsonLd(CRUMBS)],
  });
}

// The one switch for this page's light sections: these sit on the light "plan sheet"
// surface from app/styles/surfaces.css. Empty the list to put the whole page back on the
// dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["what", "gains"];
const surface = surfaceFor(LIGHT_SECTIONS);

const FIGURES = [
  ["38,000+", "people killed on US roads every year"],
  ["4.4 million", "injured seriously enough to need medical attention"],
  ["Ages 1–54", "road crashes are the leading cause of death"],
];

/**
 * A road's life, in order, and the point at which each approach finds a hazard in it.
 * `mark` puts a finding on a stage: "audit" for Road Safety Audit 3D, "crash" for a
 * crash-based program.
 */
const STAGES = [
  {
    title: "Design",
    text: "The road exists as a CAD model.",
    mark: "audit",
    finding: "Road Safety Audit 3D finds the hazard here. The fix is a design revision.",
  },
  { title: "Construction", text: "It is built as designed, hazard and all." },
  {
    title: "Opening",
    text: "Traffic arrives. For a road already in service, a LiDAR scan brings it back within reach of the audit, on any day.",
  },
  {
    title: "First crashes",
    text: "People are hurt, one at a time. No single crash marks the spot.",
  },
  {
    title: "A crash pattern",
    text: "Enough crashes accumulate to stand out in the data.",
    mark: "crash",
    finding: "A crash-based program finds it here. The fix is reconstruction.",
  },
];

const GAINS = [
  {
    title: "Before construction",
    text: "A deficiency found on a design model costs a revision. Once a road is in service, the same fix means reconstruction: the throwaway and rebuild costs an audit avoids in the first place.",
  },
  {
    title: "Without crash history",
    text: "Crash data tells you where people were already hurt. A proactive audit finds the locations that have not had their crash yet.",
  },
  {
    title: "Systemic, not spot checks",
    text: "Every lane, every direction and every intersection is analyzed the same way, so nothing depends on which stations a reviewer chose.",
  },
  {
    title: "Defensible",
    text: "Each finding cites the standard, the parameter and the 3D geometry that failed it, which stands up in a design review or a courtroom.",
  },
];

export default function Why() {
  return (
    <>
      <section className={styles.hero} aria-labelledby="why-title">
        <div className={`container ${styles.heroGrid}`}>
          <div className={styles.heroText}>
            <Breadcrumbs items={CRUMBS} />
            <h1 id="why-title" className={styles.heroTitle}>
              Why audit a road before anyone crashes on it
            </h1>
            <p className={styles.heroLead}>
              A road safety audit is a formal, independent examination of a road or a design for
              what could cause crashes. Done in 3D, it finds what plan sheets and drive-throughs
              miss.
            </p>
            <div className={styles.heroActions}>
              <Link to="/demo" className="btn btn-primary">
                Request a demo
              </Link>
              <Link to="/road-safety-audit-3d" className={styles.heroLink}>
                How Road Safety Audit 3D works
              </Link>
            </div>
          </div>
          <Picture
            src="/images/123bimlive/ssd"
            alt="A car on a divided highway, with the 250 ft stopping sight line it needs drawn ahead of it to a red target"
            ratio="16 / 9"
            widths={[480, 960]}
            fallbackWidth={960}
            sizes="(min-width: 64rem) 50vw, 100vw"
            priority
            className={styles.heroImage}
          />
        </div>
      </section>

      {/* The page's argument in one picture: when each approach finds the same hazard. */}
      <section className="section" aria-labelledby="timing-title">
        <div className="container">
          <SectionHeader
            id="timing-title"
            title="Crash data finds a hazard after the crashes"
            aside="Crash-based safety programs are reactive by construction: a location earns attention only once enough people have been hurt there. A new design has no crash history at all."
          />
          <ul className={styles.figures} aria-label="The cost of waiting for crash data">
            {FIGURES.map(([value, label]) => (
              <li key={value}>
                <strong>{value}</strong>
                <span>{label}</span>
              </li>
            ))}
          </ul>
          <ol className={styles.timeline} aria-label="When each approach finds a hazard">
            {STAGES.map((stage) => (
              <li key={stage.title} data-mark={stage.mark}>
                <span className={styles.stageTitle}>{stage.title}</span>
                <span className={styles.stageText}>{stage.text}</span>
                {stage.finding ? <p className={styles.finding}>{stage.finding}</p> : null}
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section" data-surface={surface("what")} aria-labelledby="what-title">
        <div className={`container ${styles.split}`}>
          <h2 id="what-title" className={styles.splitTitle}>
            What a road safety audit is
          </h2>
          <div className={styles.splitBody}>
            <p>
              A road safety audit (RSA) is a formal examination of a future or existing road by a
              qualified, independent, multidisciplinary team, looking for conditions that could lead
              to crashes. It considers every road user: drivers, pedestrians, cyclists and transit.
              And it is proactive: it asks what could go wrong, rather than counting what already
              has.
            </p>
            <p>
              Traditional audits rely on plan sheets, site visits and the auditors&apos; experience.
              That is thorough where the team looks and blind where it does not. Sight distance in
              particular depends on the full three-dimensional shape of the road and everything
              beside it, which is exactly what a drawing flattens.
            </p>
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="roads-title">
        <div className="container">
          <SectionHeader
            id="roads-title"
            title="New designs and existing roads"
            aside="The same analysis runs on either, so a design and the road it replaces are judged by one standard."
            link={{ to: "/road-safety-audit-3d", label: "How Road Safety Audit 3D works" }}
          />
          <div className={styles.roads}>
            <article>
              <Picture
                src="/images/site/rsa3d/planning-design"
                alt="A highway design model with the visibility of a sign measured from the driver's seat"
                ratio="16 / 9"
                widths={[480, 960]}
                fallbackWidth={960}
                sizes="(min-width: 48rem) 50vw, 100vw"
                className={styles.roadImage}
              />
              <h3>A new design</h3>
              <p>
                The audit runs on the CAD model before anything is built, so each fix is a revision
                on paper.
              </p>
            </article>
            <article>
              <Picture
                src="/images/site/rsa3d/existing-roads"
                alt="A LiDAR scan of an existing road, with its lane lines traced and the visibility measured"
                ratio="16 / 9"
                widths={[480, 960]}
                fallbackWidth={960}
                sizes="(min-width: 48rem) 50vw, 100vw"
                className={styles.roadImage}
              />
              <h3>An existing road</h3>
              <p>
                A LiDAR scan captures the road, the terrain, the vegetation and the structures as
                they are today, and shows which curves, crests and intersections fail the standard
                right now.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="section" data-surface={surface("gains")} aria-labelledby="gains-title">
        <div className="container">
          <SectionHeader
            id="gains-title"
            title="What you gain"
            aside="A Michigan State University study measured how sight distance relates to crashes, and why analyzing the whole road in 3D matters."
            link={{
              to: "/road-safety-audit-3d/sight-distance-study",
              label: "Read the sight distance study",
            }}
          />
          <ul className={styles.gains}>
            {GAINS.map((gain) => (
              <li key={gain.title}>
                <h3>{gain.title}</h3>
                <p>{gain.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CTABand
        variant="statement"
        image="/images/site/rsa3d/sight-distance"
        title="Audit a road or a design"
        text="Bring a design file or a scan, and we'll show you the findings on your own project."
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />
    </>
  );
}
